import React, { useState, useCallback } from "react"
import { SectionHeader } from "components/ui/section-header"
import { Sheet, SheetContent, SheetTitle } from "components/ui/sheet"
import type { WidgetProps } from "lib/widget-types"
import { CheckSquare, Calendar, UserPlus, ChevronRight } from "lucide-react"
import NewTaskModal from "components/tasks/NewTaskModal"
import { NewEventModal } from "components/agenda/NewEventModal"
import { NewContactModal } from "components/contacts/NewContactModal"

type ModalType = "task" | "meeting" | "contact" | null

const actions = [
  {
    title: "Nuovo Task",
    subtitle: "Crea una nuova attività",
    icon: CheckSquare,
    modal: "task" as const,
    variant: "warning" as const,
  },
  {
    title: "Pianifica Evento",
    subtitle: "Pianifica un nuovo evento",
    icon: Calendar,
    modal: "meeting" as const,
    variant: "info" as const,
  },
  {
    title: "Aggiungi Contatto",
    subtitle: "Inserisci un nuovo contatto",
    icon: UserPlus,
    modal: "contact" as const,
    variant: "success" as const,
  },
]

const variantStyles = {
  warning: {
    bgColor: "var(--hexa-warning-light)",
    borderColor: "var(--hexa-warning)",
    iconBgColor: "var(--hexa-warning)",
  },
  info: {
    bgColor: "var(--hexa-info-light)",
    borderColor: "var(--hexa-info)",
    iconBgColor: "var(--hexa-info)",
  },
  success: {
    bgColor: "var(--hexa-success-light)",
    borderColor: "var(--hexa-success)",
    iconBgColor: "var(--hexa-success)",
  },
}

export function QuickActionsWidget({ widget, onEventCreated }: { widget: WidgetProps["widget"], onEventCreated?: () => void }) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [openModal, setOpenModal] = useState<ModalType>(null)

  const handleCloseModal = useCallback(() => {
    setOpenModal(null)
  }, [])

  const handleTaskSubmit = useCallback(async (data: any) => {
    console.log("[Dashboard] Task created:", data)
    setOpenModal(null)
    return true
  }, [])

  const handleEventSave = useCallback((data: any) => {
    console.log("[Dashboard] Event created:", data)
    setOpenModal(null)
    onEventCreated?.()
  }, [onEventCreated])

  const handleContactSave = useCallback((data: any) => {
    console.log("[Dashboard] Contact created:", data)
    setOpenModal(null)
  }, [])

  return (
    <>
      <div
        className="rounded-xl backdrop-blur-sm p-4 border hexa-card-shadow h-full flex flex-col overflow-hidden"
        style={{
          backgroundColor: "var(--hexa-card-bg)",
          borderColor: "var(--hexa-border)",
        }}
      >
        <SectionHeader title={widget.title || "Azioni rapide"} showChevron={false} />
        <div className="flex flex-col gap-1.5">
          {actions.map((action, index) => {
            const styles = variantStyles[action.variant]
            const Icon = action.icon
            const isHovered = hoveredIndex === index
            return (
              <button
                key={index}
                onClick={() => setOpenModal(action.modal)}
                className="w-full flex items-center gap-2.5 p-2.5 rounded-lg border transition-colors text-left"
                style={{
                  backgroundColor: styles.bgColor,
                  borderColor: styles.borderColor,
                  opacity: isHovered ? 0.8 : 1,
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div
                  className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: styles.iconBgColor }}
                >
                  <Icon className="w-4 h-4" style={{ color: "#ffffff" }} />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-xs font-medium" style={{ color: "var(--hexa-text-primary)" }}>
                    {action.title}
                  </div>
                  <div className="text-xs font-normal truncate" style={{ color: "var(--hexa-text-secondary)" }}>
                    {action.subtitle}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <ChevronRight className="w-3.5 h-3.5" style={{ color: "var(--hexa-text-tertiary)" }} />
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Task Modal */}
      <Sheet open={openModal === "task"} onOpenChange={(open) => !open && handleCloseModal()}>
        <SheetContent className="w-full sm:max-w-[450px] p-0 bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)]">
          <SheetTitle className="sr-only">Nuovo Task</SheetTitle>
          <NewTaskModal
            isOpen={openModal === "task"}
            onClose={handleCloseModal}
            onSubmit={handleTaskSubmit}
            // FE-7: default inbox per quick actions (senza progetto specifico)
            initialProjectId={null}
            initialStatusId={null}
          />
        </SheetContent>
      </Sheet>

      {/* Event Modal */}
      <Sheet open={openModal === "meeting"} onOpenChange={(open) => !open && handleCloseModal()}>
        <SheetContent className="w-full sm:max-w-[450px] p-0 bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)]">
          <SheetTitle className="sr-only">Pianifica Evento</SheetTitle>
          <NewEventModal
            onClose={handleCloseModal}
            onSave={handleEventSave}
          />
        </SheetContent>
      </Sheet>

      {/* Contact Modal */}
      <Sheet open={openModal === "contact"} onOpenChange={(open) => !open && handleCloseModal()}>
        <SheetContent className="w-full sm:max-w-[450px] p-0 bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)]">
          <SheetTitle className="sr-only">Aggiungi Contatto</SheetTitle>
          <NewContactModal
            onClose={handleCloseModal}
            onSave={handleContactSave}
          />
        </SheetContent>
      </Sheet>
    </>
  )
}
